import * as React from 'react'
import createPersistedState from 'use-persisted-state'
import {
  CONSENT_EVENT,
  CONSENT_MAX_AGE_MS,
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
  ConsentChoices,
  ConsentRecord,
  GRANTED_CHOICES,
  REJECTED_CHOICES,
} from './types'
import { persistConsentToBackend } from './consentApi'

// Shared LocalStorage instance. `use-persisted-state` keeps its store in module
// scope, so it MUST be a shared singleton owned by the shell (see importmap in
// package.json) — otherwise a second copy would fork the store.
const useConsentState = createPersistedState(CONSENT_STORAGE_KEY)

interface ConsentContextValue {
  /** The stored decision, or null if the user has never chosen. */
  record: ConsentRecord | null
  /** True when we must (re-)ask: no record, outdated policy, or expired. */
  needsDecision: boolean
  /** Whether the preferences panel is open (manage mode). */
  isPanelOpen: boolean
  /** Open the granular preferences panel (e.g. from the footer link). */
  openPreferences: () => void
  /** Close the preferences panel without changing the stored decision. */
  closePreferences: () => void
  /** Persist an explicit, granular choice. */
  save: (choices: ConsentChoices) => void
  acceptAll: () => void
  rejectAll: () => void
}

const ConsentContext = React.createContext<ConsentContextValue | null>(null)

function isExpired (record: ConsentRecord): boolean {
  const decidedAt = Date.parse(record.decidedAt)
  if (Number.isNaN(decidedAt)) return true
  return Date.now() - decidedAt > CONSENT_MAX_AGE_MS
}

/**
 * Broadcast the effective consent so analytics/marketing loaders can react
 * without importing React. `consent/consentLoaders.ts` is the listener that turns
 * this into enforcement — it injects the analytics script only after `analytics`
 * is granted. Consent has to be *enforced*, not just recorded.
 */
function applyConsent (choices: ConsentChoices): void {
  if (typeof window === 'undefined') return
  ;(window as unknown as { __medialibConsent?: ConsentChoices }).__medialibConsent = choices
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: choices }))
}

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [record, setRecord] = useConsentState<ConsentRecord | null>(null)
  const [isPanelOpen, setPanelOpen] = React.useState(false)

  const needsDecision =
    !record || record.version !== CONSENT_VERSION || isExpired(record)

  // Re-broadcast the stored consent on every load so late-registered listeners
  // (and reloads) always see the current state. Only for a still-valid record.
  React.useEffect(() => {
    if (record && !needsDecision) applyConsent(record.choices)
  }, [record, needsDecision])

  const commit = React.useCallback((choices: ConsentChoices) => {
    const next: ConsentRecord = {
      version: CONSENT_VERSION,
      // Intl-free ISO stamp; used for audit + expiry, not display.
      decidedAt: new Date().toISOString(),
      choices,
    }
    // Pass a concrete value (not the functional-updater form): use-persisted-state
    // applies updaters against a stale snapshot and broadcasts the raw function to
    // sibling instances. See FavoriteToggle for the same foot-gun.
    setRecord(next)
    applyConsent(choices)
    // Fire-and-forget audit sync; a failure must never block the local save.
    persistConsentToBackend(next).catch(() => {})
    setPanelOpen(false)
  }, [setRecord])

  const value = React.useMemo<ConsentContextValue>(() => ({
    record: record ?? null,
    needsDecision,
    isPanelOpen,
    openPreferences: () => setPanelOpen(true),
    closePreferences: () => setPanelOpen(false),
    save: commit,
    acceptAll: () => commit(GRANTED_CHOICES),
    rejectAll: () => commit(REJECTED_CHOICES),
  }), [record, needsDecision, isPanelOpen, commit])

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
}

export function useConsent (): ConsentContextValue {
  const ctx = React.useContext(ConsentContext)
  if (!ctx) {
    throw new Error('useConsent must be used within a <ConsentProvider>')
  }
  return ctx
}
